import {
  Card,
  Text,
  Group,
  Button,
  Box,
  Stack,
  TextInput,
  ActionIcon,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { X, Plus } from 'react-feather';
import { getTimezoneAbbreviation } from '../../../utils/timezone';
import { formatDayOfWeek } from '../../../utils/formatters';
import type { SlotAvailabilityStatus, SlotConflict } from '../../../services/api/coachPortal';

export interface TimeSlotCardProps {
  slot: { id: string; day_of_week: string; time: string; timezone: string };
  // MED-1026: the configured (group-coaching) session duration in minutes,
  // used to compute the slot's end time. Sourced from the cohort's modality
  // config rather than assumed — a 75-minute session renders a 75-minute window.
  durationMinutes: number;
  status: SlotAvailabilityStatus | undefined;
  conflicts: SlotConflict[];
  onStatusChange: (status: SlotAvailabilityStatus) => void;
  onConflictsChange: (conflicts: SlotConflict[]) => void;
}

/** Parse a YYYY-MM-DD string as local midnight (avoids UTC timezone shift) */
function parseDateLocal(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function TimeSlotCard({
  slot,
  durationMinutes,
  status,
  conflicts,
  onStatusChange,
  onConflictsChange,
}: TimeSlotCardProps) {
  const isPartial = status === 'partial';
  const isAvailable = status === 'full' || status === 'partial';

  // Parse time for display
  const [hours, minutes] = slot.time.split(':').map(Number);
  const startTime = new Date();
  startTime.setHours(hours, minutes, 0, 0);
  // MED-1026: end time = start + configured session duration (was hardcoded +1h).
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

  const tzAbbr = getTimezoneAbbreviation(slot.timezone);
  const timeRange = `${startTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })} - ${endTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })} ${tzAbbr}`;

  const handleAddConflict = () => {
    onConflictsChange([...conflicts, { date: '', reason: '' }]);
  };

  const handleUpdateConflict = (index: number, field: 'date' | 'reason', value: string) => {
    const updated = [...conflicts];
    updated[index] = { ...updated[index], [field]: value };
    onConflictsChange(updated);
  };

  const handleRemoveConflict = (index: number) => {
    onConflictsChange(conflicts.filter((_, i) => i !== index));
  };

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      style={{
        borderLeftWidth: isAvailable ? 3 : 1,
        borderLeftColor: isAvailable
          ? 'var(--mantine-color-green-5)'
          : undefined,
      }}
    >
      <Group justify="space-between" align="flex-start">
        <div>
          <Text fw={500}>
            {formatDayOfWeek(slot.day_of_week)} — {timeRange}
          </Text>
          <Text size="xs" c="dimmed">
            ET / {slot.timezone}
          </Text>
        </div>
        <Group gap="xs">
          <Button
            size="xs"
            variant={status === 'full' ? 'filled' : 'outline'}
            color={status === 'full' ? 'green' : 'gray'}
            onClick={() => onStatusChange('full')}
          >
            Fully Available
          </Button>
          <Button
            size="xs"
            variant={status === 'partial' ? 'filled' : 'outline'}
            color={status === 'partial' ? 'yellow' : 'gray'}
            onClick={() => onStatusChange('partial')}
          >
            Partial
          </Button>
          <Button
            size="xs"
            variant={status === 'unavailable' ? 'filled' : 'outline'}
            color={status === 'unavailable' ? 'gray' : 'gray'}
            onClick={() => onStatusChange('unavailable')}
          >
            Unavailable
          </Button>
        </Group>
      </Group>

      {/* Conflict dates section for partial availability */}
      {isPartial && (
        <Box
          mt="md"
          p="md"
          style={{
            background: 'var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Text size="sm" fw={500} mb="sm">
            Add dates you're unavailable for this time slot:
          </Text>
          <Stack gap="sm">
            {conflicts.map((conflict, index) => (
              <Group key={index} gap="sm" align="flex-end">
                <DatePickerInput
                  value={conflict.date ? parseDateLocal(conflict.date) : null}
                  onChange={value =>
                    handleUpdateConflict(
                      index,
                      'date',
                      value ? value.split('T')[0] : '',
                    )
                  }
                  placeholder="Select date"
                  valueFormat="MM/DD/YYYY"
                  size="xs"
                  style={{ flex: 1 }}
                />
                <TextInput
                  value={conflict.reason || ''}
                  onChange={e =>
                    handleUpdateConflict(index, 'reason', e.target.value)
                  }
                  placeholder="Reason (optional)"
                  size="xs"
                  style={{ flex: 2 }}
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => handleRemoveConflict(index)}
                >
                  <X size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Button
              variant="subtle"
              size="xs"
              leftSection={<Plus size={14} />}
              onClick={handleAddConflict}
              style={{ alignSelf: 'flex-start' }}
            >
              Add conflict date
            </Button>
          </Stack>
        </Box>
      )}
    </Card>
  );
}
